import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import { FmFooter, FmDropdownInput, FmText, FmIcon, FmTermsPopup } from "../";
import { useIsMobile, useTheme } from "../../Hooks";
import { LOGGED_IN_ROUTES } from "../../Constants";
import { useTranslation } from "react-i18next";
import { FmLeftDrawer } from "../FmLeftDrawer";
import { GeneralState, AgriSolutionState, SolutionState } from "../../Redux";

import useStyles from "./FmNavigatorMenu.style";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { ABOUT_HAIFA_LINK } from "../../Utils/Consts";

interface FmNavigatorMenuProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    units: { key: string; value: string }[];
    selectedUnit: string;
    onUnitSelect: (key: string) => void;
    languages: { key: string; value: string }[];
    selectedLanguage: string;
    onLanguageChange: (key: string) => void;
    fullName?: string;
    onNameClick?: () => void;
    numberOfSavedItems?: any;
}

const FmNavigatorMenu = ({
    isOpen,
    setIsOpen,
    units,
    selectedUnit,
    onUnitSelect,
    languages,
    selectedLanguage,
    onLanguageChange,
    fullName,
    onNameClick,
    numberOfSavedItems,
}: FmNavigatorMenuProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const history = useHistory();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();

    const selectedUser = useSelector(GeneralState.Selectors.selectUser);
    const mainInterest = selectedUser?.mainInterest;
    const isAgriSolutionFirst = mainInterest === "OFA";

    const [showTerms, setShowTerms] = useState<boolean>(false);
    const [showPrivacy, setShowPrivacy] = useState<boolean>(false);

    const logoutHandler = async () => {
        dispatch(GeneralState.Actions.setGreenBar({ isShow: false, lastIsShow: false }));
        dispatch(GeneralState.Thunks.logout());
    };

    const getNavItem = (
        path: string
    ): {
        isActive: boolean;
        label: string;
        icon: string;
        path: string;
    } => {
        const currentPath = location?.pathname || "";
        let label = "";
        let icon = "";
        const isActive = currentPath.includes(path);

        switch (path) {
            case LOGGED_IN_ROUTES.AGRI_SOLUTION:
                label = t("fmLeftMenu.menuLabels.label1");
                icon = isActive || isOpen ? theme.images.navAgriActive : theme.images.navAgri;
                break;
            case LOGGED_IN_ROUTES.SOLUTION:
                label = t("fmLeftMenu.menuLabels.label2");
                icon = isActive || isOpen ? theme.images.navSolutionActive : theme.images.navSolution;
                break;
            case LOGGED_IN_ROUTES.SAVED_ITEMS:
                label = `${t("fmLeftMenu.menuLabels.label3")} (${numberOfSavedItems})`;
                icon = isActive || isOpen ? theme.images.navSavedItemsActive : theme.images.navSavedItems;
                break;
            case LOGGED_IN_ROUTES.ABOUT:
                label = t("fmLeftMenu.menuLabels.label4");
                icon = isActive || isOpen ? theme.images.navAboutActive : theme.images.navAbout;
                break;
            case LOGGED_IN_ROUTES.CONTACT_US:
                label = t("fmLeftMenu.menuLabels.label5");
                icon = isActive || isOpen ? theme.images.navContactActive : theme.images.navContact;
                break;
        }

        return {
            isActive,
            path,
            label,
            icon,
        };
    };

    const agriSolutionNavState = getNavItem(LOGGED_IN_ROUTES.AGRI_SOLUTION);
    const solutionNavState = getNavItem(LOGGED_IN_ROUTES.SOLUTION);
    const savedItemsNavState = getNavItem(LOGGED_IN_ROUTES.SAVED_ITEMS);
    const aboutNavState = getNavItem(LOGGED_IN_ROUTES.ABOUT);
    const contactUsNavState = getNavItem(LOGGED_IN_ROUTES.CONTACT_US);

    const renderItem = (data: { isActive: boolean; label: string; icon: string; path: string }) => {
        return (
            <ListItem
                button
                onClick={() => {
                    data.path == LOGGED_IN_ROUTES.ABOUT ? window.open(ABOUT_HAIFA_LINK) : !data.isActive && handleNavigationToPath(data.path);
                    isMobile && setIsOpen(false);
                }}
                className={classNames(classes.item, {
                    [classes.itemActive]: data.isActive,
                })}
            >
                <ListItemIcon className={classes.itemContainer}>
                    <FmIcon className={classes.icon} url={data.icon} title={data.label} />
                    <FmText
                        className={classNames(classes.menuLabel, {
                            [classes.menuLabelActive]: data.isActive,
                        })}
                    >
                        {data.label}
                    </FmText>
                </ListItemIcon>
            </ListItem>
        );
    };

    const renderClosedItem = (data: { isActive: boolean; label: string; icon: string; path: string }) => {
        return (
            <ListItem
                button
                onClick={() => {
                    data.path == LOGGED_IN_ROUTES.ABOUT ? window.open(ABOUT_HAIFA_LINK) : !data.isActive && handleNavigationToPath(data.path);
                }}
                className={classNames(classes.closedItem, {
                    [classes.closedItemActive]: data.isActive,
                })}
            >
                <ListItemIcon className={classes.itemContainer}>
                    <div className={classes.closedItemIconContainer}>
                        <FmIcon url={data.icon} title={data.label} />
                    </div>
                </ListItemIcon>
            </ListItem>
        );
    };

    const handleNavigationToPath = async (path: any) => {
        if (path == LOGGED_IN_ROUTES.AGRI_SOLUTION) {
            await dispatch(GeneralState.Actions.setIsAgri(true));
        } else if (path == LOGGED_IN_ROUTES.SOLUTION) {
            await dispatch(GeneralState.Actions.setIsAgri(false));
        }
        reset();
        history.push(path);
    };
    const closeHandle = () => {
        isMobile && setIsOpen(false);
    };

    const reset = () => {
        dispatch(GeneralState.Actions.setGreenBar({ isShow: false, lastIsShow: false }));
        dispatch(AgriSolutionState.Actions.setDisplayUpdateButton(false));
        dispatch(AgriSolutionState.Actions.setItemId(null));
        dispatch(SolutionState.Actions.setItemId(null));
        dispatch(AgriSolutionState.Actions.reset());
        dispatch(SolutionState.Actions.reset());
        dispatch(GeneralState.Actions.setSummaryDescription(""));
    };
    const agriIsMainInterest = useSelector(GeneralState.Selectors.selectAgriIsMainInterestOfUser);

    const defaultPath = agriIsMainInterest ? LOGGED_IN_ROUTES.AGRI_SOLUTION : LOGGED_IN_ROUTES.SOLUTION;

    const logoClickHandler = () => {
        reset();
        isMobile && setIsOpen(false);
        history.push(defaultPath);
    };

    const renderMobileHeader = () => {
        return (
            <>
                <div onClick={closeHandle}>
                    <ListItem button onClick={onNameClick} className={classes.item}>
                        <ListItemIcon className={classes.itemContainer}>
                            <FmIcon className={classes.icon} url={isMobile ? theme.images.userGreen : theme.images.user} title={fullName} />
                            <FmText className={classes.menuLabel}> {fullName} </FmText>
                        </ListItemIcon>
                    </ListItem>
                </div>
                <ListItem button className={classes.item}>
                    <ListItemIcon className={classes.itemContainer}>
                        <FmIcon className={classes.icon} url={isMobile ? theme.images.earthGreen : theme.images.earth} title={"language"} />
                        <FmDropdownInput options={languages} onOptionSelect={onLanguageChange} selectedOptionKey={selectedLanguage} />
                    </ListItemIcon>
                </ListItem>
                <ListItem button className={classes.item}>
                    <ListItemIcon className={classes.itemContainer}>
                        <FmIcon className={classes.icon} url={isMobile ? theme.images.unitsGreen : theme.images.units} title={"units"} />
                        <FmDropdownInput options={units} onOptionSelect={onUnitSelect} selectedOptionKey={selectedUnit} />
                    </ListItemIcon>
                </ListItem>
            </>
        );
    };

    const renderOpenNavbar = () => {
        return (
            <div className={classes.openContainer}>
                <div>
                    <div className={classes.logoContainer} onClick={logoClickHandler}>
                        <img className={classes.logo} src={theme.images.bigLogo} alt='haifa' title='haifa' />
                        <div className={classes.titleContainer}>
                            <FmText className={classes.title}>{t("fmLeftMenu.title")}</FmText>
                        </div>
                        <div className={classes.subTitleContainer}>
                            <FmText className={classes.subTitle}>{t("fmLeftMenu.subTitle")}</FmText>
                        </div>
                    </div>
                    {isAgriSolutionFirst ? (
                        <div className={classes.navContainer}>
                            {isMobile && renderMobileHeader()}
                            {renderItem(agriSolutionNavState)}
                            {renderItem(solutionNavState)}
                            {renderItem(savedItemsNavState)}
                            {renderItem(aboutNavState)}
                            {renderItem(contactUsNavState)}
                        </div>
                    ) : (
                        <div className={classes.navContainer}>
                            {isMobile && renderMobileHeader()}
                            {renderItem(solutionNavState)}
                            {renderItem(agriSolutionNavState)}
                            {renderItem(savedItemsNavState)}
                            {renderItem(aboutNavState)}
                            {renderItem(contactUsNavState)}
                        </div>
                    )}
                </div>
                <div className={isMobile ? classes.logout_container_mobile : classes.logout_container} onClick={logoutHandler}>
                    {t("fmLeftMenu.logout")}
                </div>
                <div className={classes.footer}>
                    <FmFooter setShowTerms={setShowTerms} setShowPrivacy={setShowPrivacy} />
                </div>
                <div className={classes.termsAndPrivacy_container}>
                    <FmTermsPopup
                        title={t("sideMenu.termsAndCondition.contentTitle")}
                        description={t("sideMenu.termsAndCondition.data")}
                        isShow={showTerms}
                        setIsShow={setShowTerms}
                    />
                </div>
                <div className={classes.termsAndPrivacy_container}>
                    <FmTermsPopup
                        title={t("sideMenu.privacyPolicy.title")}
                        description={t("sideMenu.privacyPolicy.data")}
                        isShow={showPrivacy}
                        setIsShow={setShowPrivacy}
                    />
                </div>
            </div>
        );
    };

    const renderClosedNavbar = () => {
        return (
            <div className={classes.closedContainer}>
                <div>
                    <div onClick={logoClickHandler} className={classes.closedLogoContainer}>
                        <img className={classes.logo} src={theme.images.bigLogo} alt='haifa' title='haifa' />
                    </div>
                    <div className={classes.closedTitleContainer} onClick={logoClickHandler}>
                        <FmText className={classes.closedTitle}>{t("fmLeftMenu.title")}</FmText>
                    </div>
                    {isAgriSolutionFirst ? (
                        <div className={classes.closedNavContainer}>
                            {renderClosedItem(agriSolutionNavState)}
                            {renderClosedItem(solutionNavState)}
                            {renderClosedItem(savedItemsNavState)}
                            {renderClosedItem(aboutNavState)}
                            {renderClosedItem(contactUsNavState)}
                        </div>
                    ) : (
                        <div className={classes.closedNavContainer}>
                            {renderClosedItem(solutionNavState)}
                            {renderClosedItem(agriSolutionNavState)}
                            {renderClosedItem(savedItemsNavState)}
                            {renderClosedItem(aboutNavState)}
                            {renderClosedItem(contactUsNavState)}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={classes.container}>
            <FmLeftDrawer isOpen={isOpen} setIsOpen={(i: boolean) => setIsOpen && setIsOpen(i)} isMobile={isMobile}>
                {isOpen && renderOpenNavbar()}
                {!isOpen && renderClosedNavbar()}
            </FmLeftDrawer>
        </div>
    );
};

export default FmNavigatorMenu;
